import GenericCrudTable from "@/components/tables/genericCrudTable";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { Department } from "@/components/userAdministration/administration";
import { Chip, Tooltip, Typography } from "@mui/material";
import styles from "@/components/userAdministration/departmentTable.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { useMemo, useState } from "react";
import Modal from "@/components/modal";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { api } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { departmentEntityAdapter } from "@/components/userAdministration/departmentEntityAdapter";

interface Props {
  companySubjectId?: string; //only present if Admin
  companyId?: string;
  uploadSuccessful?: boolean;
  handleDepartmentForFilter: (row: any) => any;
  department?: Department[];
  handleDeleteDepartment?: () => void;
  handleHasDepartments?: (value: boolean) => void;
  subdomain: any;
  handleRefreshUserTable: () => void;
  handleError: (error: any) => void;
  handleNotification: (message: any, variant: any, show: boolean) => void;
}

export default function DepartmentTable(props: Props) {
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState<any>(false);
  const [selectedCustomers, setSelectedCustomers] = useState<any[]>([]);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [departmentName, setDepartmentName] = useState<string | null>(null);
  const [customerSelectionError, setCustomerSelectionError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "customers">({
      fieldOrder: [],
    });

  const { t } = useTranslation();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const user = useSelector((state: RootState) => state.login);

  const isDepartmentManager = user?.group_set.includes("manager") && false;

  const additionalQuickSearchParameter: any = {};
  additionalQuickSearchParameter["company_id"] = props.companyId; //TODO @Ben, we check the existence of companyId below, why not here?

  const additionalUrlParameter = useMemo(
    () => ({ include_user_count: true }),
    [],
  );

  const handleModalStatus = (status: boolean, row: any) => {
    setAddCustomerModalOpen(status);
    setCustomerSelectionError(false);

    if (row) {
      setDepartmentId(row.id);
      setDepartmentName(row.name);
    }
  };

  const customRowAction = (row: any) => {
    return (
      <Tooltip
        arrow
        placement="top"
        enterDelay={1000}
        enterNextDelay={250}
        title={t("add_customer")}
      >
        <GenericButton
          variant="icon"
          color="iconBase"
          onClick={() => handleModalStatus(true, row.original)}
        >
          <AddIcon />
        </GenericButton>
      </Tooltip>
    );
  };

  const saveDropDownOnChangeData = (saveMethod: any, data: any) => {
    if (data) {
      if (typeof data === "object" && data.value) {
        return saveMethod(data.value);
      }

      if (Array.isArray(data) && data.length > 0) {
        const tempList = data.map((item) => item.value);
        return saveMethod(tempList);
      } else saveMethod([]);
    }
  };
  const handleSubjectSetChange = (selectedOption: any) => {
    setCustomerSelectionError(false);
    saveDropDownOnChangeData(setSelectedCustomers, selectedOption);
  };

  const onSubmitModalData = async () => {
    if (!selectedCustomers || selectedCustomers.length === 0) {
      setCustomerSelectionError(true);
      focusFirstInvalidField({}, ["customers"]);
      return;
    }

    const confirmAddCustomersToDepartment = await showConfirmationModalAsync(
      t("confirm_add_customers_to_department", {
        number: selectedCustomers.length,
        department: departmentName,
      }),
    );

    if (!confirmAddCustomersToDepartment) return;

    const additionalRouteParts: any = {};
    additionalRouteParts["add_new_subjects"] = "/";
    const dataToSubmit = { subject_set: selectedCustomers };
    api.genericApiRequest({
      entity: "department",
      method: "put",
      submitData: dataToSubmit,
      entityId: departmentId,
      additionalRouteParts: additionalRouteParts,
      successHandler: () => {
        const notificationMessage = t(
          "customers_successfully_added_to_department",
          {
            number: selectedCustomers.length,
            department: departmentName,
          },
        );
        props.handleNotification(notificationMessage, "success", true);

        handleModalStatus(false, null);
        props.handleRefreshUserTable();
      },
      failHandler: (error: any) => {
        props.handleError(error);
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        header: t("id"),
        accessorKey: "id",
        id: "id",
        enableHiding: !props.companySubjectId ? false : true,
        visibleInShowHideMenu: !props.companySubjectId ? false : true,
      },
      {
        header: t("name"),
        accessorKey: "name",
        id: "name",
        enableForCreate: true,
        required: true,
        errorMessage: t("field_required"),
        validate: true,
        rule: /^[a-zA-ZÄäÖöÜüß0-9 _&()+/-]+$/,
        errorMessageValidation: t("invalid_characters"),
        autoComplete: "off",
        size: 300,
        Cell: ({ cell, row }: any) => {
          return (
            <>
              {props.department &&
              props.department?.length !== 0 &&
              props.department[0].departmentId === row.original.id ? (
                <Chip
                  label={props.department?.[0].departmentName}
                  onDelete={props.handleDeleteDepartment}
                  className={styles.chip}
                  classes={{ deleteIcon: styles.deleteIcon }}
                />
              ) : (
                <span
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => props.handleDepartmentForFilter?.(row)}
                >
                  {cell.getValue()}
                </span>
              )}
            </>
          );
        },
      },
      {
        header: t("customer"),
        accessorKey: "user_count",
        id: "user_count",
        customColumnFilterId: "user_count",
        customSortingKey: "user_count",
      },
      {
        header: t("building_department"),
        accessorKey: "building",
        id: "building",
        customColumnFilterId: "building__name",
        enableForCreate: true,
        showAddressForBuildingSelect: true,
        entity: "building",
        accessorFn: (row: any) => {
          if (
            row.building &&
            (row.main_building !== null || row.main_building !== undefined)
          ) {
            const building = row.building;
            return (
              <>
                <span>{building.name}</span>
              </>
            );
          }
        },
      },
      {
        header: t("locked_for_user_csv_label"),
        accessorKey: "locked_for_user_csv",
        id: "locked_for_user_csv",
        enableForCreate: true,
        checkBox: true,
        required: false,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: t("yes") },
          { value: "false", label: t("no") },
        ],
        Cell: ({ cell }: any) =>
          cell.getValue() === true || cell.getValue() === "true"
            ? t("yes")
            : t("no"),
      },
      ...(props.subdomain === "admin" // the api delivers the tracking fields only on the admin subdomain
        ? getRecordMetadataColumns(t, [
            "created_on",
            "created_by",
            "modified_on",
            "modified_by",
          ])
        : []),
    ],
    [
      t,
      props.subdomain,
      props.companySubjectId,
      props.department,
      props.handleDeleteDepartment,
      props.handleDepartmentForFilter,
    ],
  );

  return (
    <>
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        customRowActions={customRowAction}
        entity={"department"}
        entityAdapter={departmentEntityAdapter}
        columns={columns}
        companySubjectId={props.companySubjectId}
        companyId={props.companyId}
        uploadSuccessful={props.uploadSuccessful}
        onSuccessfulGetHandler={(data) =>
          props.handleHasDepartments?.(data.results.length !== 0)
        }
        additionalUrlParameter={additionalUrlParameter}
        readOnlyMode={isDepartmentManager}
      />
      <Modal
        open={addCustomerModalOpen}
        onClose={() => handleModalStatus(false, null)}
        onSubmit={onSubmitModalData}
        title={t("add_customer")}
      >
        <div ref={registerValidationTarget("customers")}>
          <FormFieldLabel
            label={t("employee_search_hint")}
            htmlFor="department-customer-search"
          />
          <DropDownSubjectSearch
            name="department-customer-search"
            options={[]}
            useGlobalState={false}
            subdomain={props.subdomain}
            allowSubjectSearchForNonAdmin={false}
            queryParameter="users"
            companyIdForFiltering={props.companyId}
            isMulti={true}
            onChange={handleSubjectSetChange}
            error={customerSelectionError}
          />
          {customerSelectionError && (
            <Typography color="error" variant="caption" role="alert">
              {t("field_required")}
            </Typography>
          )}
        </div>
      </Modal>
    </>
  );
}
